import cx from "classnames";

import useNav from "utils/useNav";
import ExpandedNav from "./ExpandedNav";
import Search from "./Search";
import NavAccountLinks from "./NavAccountLinks";
import NavHamburgerButton from "./NavHamburgerButton";
import NavSearchButton from "./NavSearchButton";
import Logo from "assets/atlantic-logo.svg";
import BigA from "assets/big-a.svg";

import styles from "./Nav.module.scss";

const Nav = () => {
  const {
    refs: {
      navRef,
      expandedNavRef,
      desktopSearchRef,
      searchButtonRef,
      hamburgerButtonRef,
    },
    state: {
      navIsExpanded,
      searchIsExpanded,
      overlayOpen,
      isDesktop,
      isMobile,
    },
    actions: {
      openNav,
      closeNav,
      openDesktopSearch,
      closeDesktopSearch,
      openMobileSearch,
      closeMobileSearch,
    },
  } = useNav();

  return (
    <>
      <nav
        ref={navRef}
        className={cx(styles.root, navIsExpanded && styles.isExpanded)}
        aria-labelledby="site-navigation"
        data-category="Site Nav"
        id="main-navigation"
      >
        <a href="#main-content" className={styles.skipLink}>
          Skip to content
        </a>
        <h2 id="site-navigation" className={styles.visuallyHide}>
          Site Navigation
        </h2>
        <div className={styles.flexContainer}>
          <ul className={styles.leftContainer}>
            <li className={cx(styles.navListItem, styles.visuallyHideOnMobile)}>
              <a href="https://www.theatlantic.com" className={styles.navLink}>
                <BigA title="The Atlantic" className={styles.bigA} />
              </a>
            </li>
            <li className={cx(styles.navListItem, styles.hamburgerLi)}>
              <NavHamburgerButton
                navIsExpanded={navIsExpanded}
                openNav={openNav}
                closeNav={closeNav}
                hamburgerButtonRef={hamburgerButtonRef}
              />
              <div className={styles.expandedNav}>
                <ExpandedNav
                  navIsExpanded={navIsExpanded}
                  searchIsExpanded={searchIsExpanded}
                  openMobileSearch={openMobileSearch}
                  closeMobileSearch={closeMobileSearch}
                  ref={expandedNavRef}
                  isMobile={isMobile}
                  id="expanded-nav"
                />
              </div>
            </li>
            <li
              className={cx(
                styles.navListItem,
                styles.hideOnTablet,
                styles.searchLi,
                searchIsExpanded && styles.bigBoi
              )}
            >
              <NavSearchButton
                searchIsExpanded={searchIsExpanded}
                openDesktopSearch={openDesktopSearch}
                closeDesktopSearch={closeDesktopSearch}
                searchButtonRef={searchButtonRef}
              />
              <Search
                searchIsExpanded={searchIsExpanded && isDesktop}
                closeSearch={() => {
                  closeDesktopSearch();
                  searchButtonRef.current.focus({ preventScroll: true });
                }}
                id="nav-desktop-search"
                ref={desktopSearchRef}
              />
            </li>
            <li
              hidden={searchIsExpanded}
              className={cx(
                styles.navListItem,
                styles.hideOnTablet,
                searchIsExpanded && styles.displayNone
              )}
            >
              <a
                className={styles.navLink}
                href="https://www.theatlantic.com/most-popular/"
                data-action="popular"
              >
                Popular
              </a>
            </li>
            <li
              className={cx(
                styles.navListItem,
                styles.hideOnTablet,
                searchIsExpanded && styles.displayNone
              )}
            >
              <a
                className={styles.navLink}
                href="https://www.theatlantic.com/latest/"
                data-action="latest"
              >
                Latest
              </a>
            </li>
          </ul>
          {/**
           * Hide the full logo for assistive tech because the big A logo
           * is better placed semantically for a "Home" link, and the full logo
           * link is duplicative content.
           * See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-hidden_attribute
           *
           * Since both logos are hidden, we need to make them unfocusable
           * in the tab order.
           * See: https://dequeuniversity.com/rules/axe/3.3/aria-hidden-focus
           */}
          <div aria-hidden={true} className={styles.middleContainer}>
            <a
              href="https://www.theatlantic.com"
              className={cx(styles.hideAboveMobile, styles.mobileBigALink)}
              tabIndex={-1}
              data-action="big a"
            >
              <BigA className={styles.mobileBigA} />
            </a>
            <a
              href="https://www.theatlantic.com"
              className={styles.navLink}
              tabIndex={-1}
              data-action="logo"
            >
              <Logo className={styles.logo} />
            </a>
          </div>
          <div className={styles.rightContainer}>
            <NavAccountLinks
              navRef={navRef}
              navIsExpanded={navIsExpanded}
              searchIsExpanded={searchIsExpanded}
              closeNav={closeNav}
              closeDesktopSearch={closeDesktopSearch}
            />
          </div>
        </div>
      </nav>
      {/*
       * We can disable the `no-static-element-interactions` warning here
       * because our overlay is only designed to capture outside
       * clicks, not to serve as a clickable element itself.
       *
       * We can disable the `click-events-have-key-events` warning because
       * we listen for an Escape keydown event in the `useEffect` hook
       */}
      {/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={cx(styles.overlay, overlayOpen && styles.overlayOpen)}
        data-testid="overlay"
        onClick={() => {
          if (navIsExpanded) closeNav();
          if (searchIsExpanded) closeDesktopSearch();
        }}
      />
      {/* eslint-enable */}
    </>
  );
};

export default Nav;
